// Push to Autotask — the review modal.
//
// Craig: "a popup after I click Push to Autotask, that shows the title of
// ticket, description and ability to add check list items… The title will be
// the Quote header description, the description should list each accepted
// item description with quantity line by line, and if a labour line is
// included, the full description of the labour."
//
// Same shape as the send modal: nothing reaches Autotask until Craig presses
// the button here. The backend's /push-autotask/preview SEEDS the title and
// description (quoteTicket.js owns the words, and the never-print rule); this
// modal lets him rewrite both freely and add free-text checklist items.
//
// ⚠ THE BASIS IS SAID OUT LOUD. An accepted quote's text comes from the frozen
// acceptance snapshot — what the client actually chose — and the banner says
// so. An unaccepted one comes from the options currently selected, and the
// banner says he is pushing a proposal, not an order.
//
// ⚠ CHECKLIST ITEMS ARE VERIFIED, NOT ASSUMED. TicketChecklistItems proved
// readable through the API (2026-09-20), so after the push the backend reads
// the ticket's items back and the result panel shows "N of M verified on the
// ticket". If the read-back itself fails, that is shown as "posted but could
// not be verified" — never as silence.
//
// ISSUE TYPE · SUB-ISSUE TYPE (2026-10-02): the shared TicketClassificationFields
// over useTicketPicklists (one combined fetch per quote; see those files for
// the parent/child, inactive and degrade-don't-block rules). Source is set
// server-side (Web Portal), not here.
import React, { useEffect, useState } from "react";
import { useTicketPicklists } from "./useTicketPicklists";
import { idOrNull } from "./ticketPicklists";
import TicketClassificationFields from "./TicketClassificationFields";

function whenPerth(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-AU", { timeZone: "Australia/Perth", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function QuotePushModal({ quote, preview, busy, result, onCancel, onPush }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [items, setItems] = useState([]);
  const [force, setForce] = useState(false);
  const [classification, setClassification] = useState({ issueType: "", subIssueType: "" });
  const { picklists, error: picklistsError } = useTicketPicklists(quote?.id);

  /* Seed from the preview when it arrives (or changes quote). Editing after
     that is Craig's; a re-seed only happens for a different quote. */
  useEffect(() => {
    if (!preview) return;
    setTitle(preview.title || "");
    setDescription(preview.description || "");
    setItems([]);
    setForce(false);
    setClassification({ issueType: "", subIssueType: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id, preview]);

  if (!quote) return null;

  const basis = preview?.basis || null;
  const already = preview?.alreadyPushed || null;
  const canPush = !!preview && !busy && !result && title.trim().length > 0 && (!already || force);
  const cleanItems = items.map((s) => s.trim()).filter(Boolean);

  const setItem = (i, v) => setItems((prev) => prev.map((x, j) => (j === i ? v : x)));
  const removeItem = (i) => setItems((prev) => prev.filter((_, j) => j !== i));

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(11,34,64,0.45)", zIndex: 500,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
         onClick={busy ? undefined : onCancel}>
      <div style={{ background: "var(--s1)", borderRadius: 10, padding: 22, width: 680,
                    maxWidth: "100%", maxHeight: "92vh", overflowY: "auto", border: "1px solid var(--border)" }}
           onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 15, color: "var(--text)", marginBottom: 4 }}>
          Push to Autotask — {quote.companyName || "the client"}
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 12, lineHeight: 1.6 }}>
          Creates one ticket in the <strong>Triage</strong> queue on the linked company. Nothing is sent until you press Push.
        </div>

        {!preview && !result && (
          <div style={{ fontSize: 12, color: "var(--text2)", padding: "18px 0" }} data-testid="push-loading">Building the ticket from the quote…</div>
        )}

        {preview && !result && (
          <>
            {/* The basis, in words, before anything else. */}
            {basis?.kind === "accepted" ? (
              <div data-testid="push-basis" style={{ fontSize: 11, background: "#dcfce7", border: "1px solid #86efac", color: "#166534", borderRadius: 6, padding: "6px 10px", marginBottom: 10, lineHeight: 1.5 }}>
                <strong>Built from what the client accepted</strong>
                {basis.acceptedBy ? ` — ${basis.acceptedBy}` : ""}{basis.acceptedAt ? `, ${whenPerth(basis.acceptedAt)}` : ""}.
                {" "}Their option choices and quantities are already applied; this is what to order.
              </div>
            ) : (
              <div data-testid="push-basis" style={{ fontSize: 11, background: "#fef3c7", border: "1px solid #fcd34d", color: "#92400e", borderRadius: 6, padding: "6px 10px", marginBottom: 10, lineHeight: 1.5 }}>
                <strong>Not accepted yet.</strong> Built from the options currently selected in the builder — you are pushing a proposal, not an order.
              </div>
            )}

            {already && (
              <div data-testid="push-already" style={{ fontSize: 11, background: "#fee2e2", border: "1px solid #fca5a5", color: "#991b1b", borderRadius: 6, padding: "6px 10px", marginBottom: 10, lineHeight: 1.5 }}>
                <strong>Already pushed</strong> as {already.autotaskTicketNumber || `ticket ${already.autotaskTicketId}`}
                {already.autotaskPushedAt ? ` on ${whenPerth(already.autotaskPushedAt)}` : ""}. Pushing again creates a second ticket.
                <label style={{ display: "block", marginTop: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} style={{ marginRight: 6 }} />
                  Push again anyway
                </label>
              </div>
            )}

            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)" }}>Ticket title</label>
            <input className="inp" aria-label="Ticket title" value={title} onChange={(e) => setTitle(e.target.value)}
                   disabled={!!busy} style={{ fontSize: 12, marginBottom: 10 }} />

            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)" }}>Description</label>
            <textarea className="inp" aria-label="Description" rows={14} value={description}
                      onChange={(e) => setDescription(e.target.value)} disabled={!!busy}
                      style={{ fontSize: 11.5, lineHeight: 1.5, resize: "vertical", fontFamily: "ui-monospace, Menlo, Consolas, monospace", marginBottom: 10 }} />

            {/* Classification — above the checklist, because it says what the
                ticket IS; the checklist says what to do about it. */}
            <TicketClassificationFields picklists={picklists} error={picklistsError} disabled={!!busy} idPrefix="push"
                                        unavailableTestId="push-ref-unavailable"
                                        issueType={classification.issueType} subIssueType={classification.subIssueType}
                                        onChange={setClassification} />

            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)" }}>Checklist items</label>
            <div style={{ fontSize: 10, color: "var(--text3)", marginBottom: 6 }}>
              Added to the ticket's checklist, in this order. Leave empty for none.
            </div>
            {items.map((it, i) => (
              <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: "var(--text3)", width: 18, textAlign: "right", flexShrink: 0 }}>{i + 1}.</span>
                <input className="inp" aria-label={`Checklist item ${i + 1}`} value={it} placeholder="e.g. Confirm the switch is racked"
                       onChange={(e) => setItem(i, e.target.value)} disabled={!!busy}
                       style={{ fontSize: 12, marginBottom: 0, flex: 1 }} />
                <button className="btn" style={{ fontSize: 11, padding: "6px 9px" }} title="Remove this item" disabled={!!busy}
                        onClick={() => removeItem(i)}>×</button>
              </div>
            ))}
            <button className="btn" style={{ fontSize: 11, marginBottom: 14 }} disabled={!!busy}
                    onClick={() => setItems((prev) => [...prev, ""])}>+ Add checklist item</button>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
              <span style={{ fontSize: 10, color: "var(--text3)", marginRight: "auto" }}>Queue: Triage · Source: Web Portal</span>
              <button className="btn" style={{ fontSize: 11 }} onClick={onCancel} disabled={!!busy}>Cancel</button>
              <button className="btn btn-strat" style={{ fontSize: 11 }} disabled={!canPush}
                      onClick={() => onPush({
                        title: title.trim(), description, checklist: cleanItems, force,
                        issueType: idOrNull(classification.issueType), subIssueType: idOrNull(classification.subIssueType),
                      })}>
                {busy ? "Pushing…" : `Push to Autotask${cleanItems.length ? ` with ${cleanItems.length} checklist item${cleanItems.length === 1 ? "" : "s"}` : ""}`}
              </button>
            </div>
          </>
        )}

        {result && (
          <div data-testid="push-result">
            <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d", background: "#dcfce7", border: "1px solid #86efac", borderRadius: 6, padding: "8px 10px", marginBottom: 10 }}>
              Created {result.autotaskTicketNumber || `ticket ${result.autotaskTicketId}`} in Triage.
            </div>
            {result.checklist && result.checklist.requested > 0 && (
              <div style={{ fontSize: 11, lineHeight: 1.6, marginBottom: 10 }}>
                {result.checklist.verifyError ? (
                  <div style={{ color: "#92400e", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, padding: "6px 10px" }}>
                    {result.checklist.created} of {result.checklist.requested} checklist items were posted, but they <strong>could not be read back to verify</strong>: {result.checklist.verifyError}. Check the ticket's Checklist tab.
                  </div>
                ) : (
                  <div style={{ color: result.checklist.verified === result.checklist.requested ? "#166534" : "#92400e" }}>
                    <strong>{result.checklist.verified} of {result.checklist.requested}</strong> checklist items verified on the ticket.
                  </div>
                )}
                {result.checklist.failed && result.checklist.failed.length > 0 && (
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18, color: "#991b1b" }}>
                    {result.checklist.failed.map((f, i) => <li key={i}>Not added: “{f.itemName}” — {f.error}</li>)}
                  </ul>
                )}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-strat" style={{ fontSize: 11 }} onClick={onCancel}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
